import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { message, Select, Button } from "antd";
import { EyeOutlined, SyncOutlined } from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  AllCommunityModule,
  ModuleRegistry,
  provideGlobalGridOptions,
} from "ag-grid-community";
import { fetchTransactions, syncTransactions } from "../../core/api/data";
import { fetchBrandsBySelectedClients } from "../../core/api/settings";
import { useSelector } from "react-redux";
import formatDateWithTimezone from "../../utils/dateUtils";
import {dateFilterParams} from "../../utils/dateUtils";

ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

const TransactionsList = ({ onViewDetails, timezone, filters, refreshTrigger }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncInProgress, setSyncInProgress] = useState(false);
  // We'll use brands from filters instead of maintaining local state
  const [lastSyncTime, setLastSyncTime] = useState(null);
    
  // Initial data load when component mounts
  useEffect(() => {
    if (filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      loadTransactions(filters.brands[0], filters);
    } else {
      setLoading(false);
    }
  }, [filters]);
  
  useEffect(() => {
    if (refreshTrigger > 0 && filters && filters.brands && filters.brands.length > 0) {
      setLoading(true);
      loadTransactions(filters.brands[0], filters);
    }
  }, [refreshTrigger]);
  
  const loadTransactions = async (brandId, currentFilters = null) => {
    setLoading(true);
    try {
      const response = await fetchTransactions(brandId, currentFilters);
      
      if (response.results) {
        setTransactions(response.results);
        
        if (response.last_sync_time) {
          const syncDate = new Date(response.last_sync_time);
          setLastSyncTime(syncDate.toLocaleString());
        }
      } else {
        setTransactions(response);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      message.error("Failed to fetch transactions.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleSyncTransactions = () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.warning("Please select a brand first.");
      return;
    }
    
    const brandId = filters.brands[0];
    setSyncInProgress(true);
    message.loading({ content: "Syncing transactions...", key: "syncMessage", duration: 0 });
    
    syncTransactions(brandId)
      .then(response => {
        message.success({ content: "Transactions synced successfully!", key: "syncMessage" });
        console.log('Sync response:', response); // Debug log
        
        // Check if the response contains last_sync_time
        if (response.last_sync_time) {
          const syncDate = new Date(response.last_sync_time);
          setLastSyncTime(syncDate.toLocaleString());
        }
        
        // Refresh transactions after sync
        return fetchTransactions(brandId);
      })
      .then(response => {
        console.log('Refresh response after sync:', response); // Debug log
        
        // Check if the response has a nested structure with results and last_sync_time
        if (response.results) {
          setTransactions(response.results);
          
          if (response.last_sync_time) {
            const syncDate = new Date(response.last_sync_time);
            setLastSyncTime(syncDate.toLocaleString());
          }
        } else {
          // If response is just an array of transactions
          setTransactions(response);
        }
        setSyncInProgress(false);
      })
      .catch(error => {
        console.error("Error syncing transactions:", error);
        message.error({ content: "Failed to sync transactions.", key: "syncMessage" });
        setSyncInProgress(false);
      });
  };
  
  // View transaction details handler
  const handleViewDetails = (transaction) => {
    if (onViewDetails && transaction) {
      onViewDetails(transaction);
    }
  };
  
  // Cell renderer for the View Details button
  const ViewDetailsButton = (params) => {
    return (
      <Button 
        icon={<EyeOutlined />} 
        type="primary" 
        size="small"
        onClick={() => handleViewDetails(params.data)}
      >
        View Details
      </Button>
    );
  };

  const transactionColumns = [
    {
      headerName: "Gateway Account Name",
      field: "gateway_account_name",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Transaction ID",
      field: "transId",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Batch ID",
      field: "batch",
      sortable: true,
      filter: true,
      width: 150,
      valueGetter: (params) => {
        if (!params.data.batch) return "Unsettled";
        return params.data.batch;
      },
    },
    {
      headerName: "Submit Time",
      field: "submitTimeUTC",
      sortable: true,
      filter: 'agDateColumnFilter',  filterParams: dateFilterParams,
      width: 220,
      resizable: true,
      valueFormatter: (p) => formatDateWithTimezone(p.value, timezone),
    },
    {
      headerName: "Status",
      field: "transactionStatus",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Name",
      valueGetter: (params) => {
        if (!params.data) return "";
        return `${params.data.firstName || ''} ${params.data.lastName || ''}`;
      },
      sortable: true,
      filter: true,
      width: 180,
    },
    {
      headerName: "Account Number",
      field: "accountNumber",
      sortable: true,
      filter: true,
      width: 180,
      valueFormatter: (params) => {
        if (!params.value) return "";
        // Mask all but last 4 digits
        return `xxxx-xxxx-xxxx-${params.value.slice(-4)}`;
      },
    },
    {
      headerName: "Amount",
      field: "settleAmount",
      sortable: true,
      filter: true,
      width: 120,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return "";
        return `$${parseFloat(params.value).toFixed(2)}`;
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      sortable: false,
      filter: false,
      width: 120,
      cellRenderer: ViewDetailsButton,
    },
  ];

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="card-title mb-0">Transactions</h4>
          <div className="d-flex align-items-center">
            {lastSyncTime && (
              <div className="me-3">
                <small>Last synced: {lastSyncTime}</small>
              </div>
            )}
            {/* Brand selection is now handled by filters */}
            <Button
              type="primary"
              icon={<SyncOutlined spin={syncInProgress} />}
              loading={syncInProgress}
              onClick={handleSyncTransactions}
              disabled={!filters?.brands?.length || syncInProgress}
            >
              Sync
            </Button>
          </div>
        </div>
        
        {loading ? (
          <div className="text-center p-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
            <AgGridReact
              rowData={transactions}
              columnDefs={transactionColumns}
              pagination={false}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
              }}
              sortModel={[{
                colId: 'submitTimeUTC',
                sort: 'desc'
              }]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsList;
